import { Component, OnInit, AfterViewInit, Renderer2, ElementRef, ViewChild, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { environment } from 'src/environments/environment';
// @ts-ignore
import vegaEmbed from 'vega-embed';

// Type declarations for vega
declare const vega: any;
declare const vegaLite: any;

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule
  ]
})
export class DashboardComponent implements OnInit, AfterViewInit {
  searchInput: string = '';
  selectedLos: string = '';
  pvNonPvFilter: string = '';
  losOptions: string[] = [];
  legendItems: { label: string, color: string, sitesCount: number, usersCount: number }[] = [];
  isLoading: boolean = false;

  allSites: any[] = [];
  filteredData: any[] = [];
  currentChunk: number = 0;
  barStep: number = 20;
  CHUNK_SIZE = 70;
  PAGE_SIZE = 1999;
  CACHE_KEY = 'cachedSites';
  losColorMap = {
    'Enterprise': '#3b82f6',
    'Fixed Bronze': '#cd7f32',
    'Gold': '#fbbf24',
    'Mobile Bronze': '#a0522d',
    'Silver': '#a0aec0',
  };
  selectedLosFilter: string | null = null;
  hasCachedSites: boolean = false;

  @ViewChild('userModal') userModalTpl!: TemplateRef<any>;
  @ViewChild('chartContainer') chartContainer!: ElementRef;

  constructor(private renderer: Renderer2, private el: ElementRef, private dialog: MatDialog) { }

  ngOnInit(): void {
    this.buildLegendItems([]);
    const cached = localStorage.getItem(this.CACHE_KEY);
    this.hasCachedSites = !!cached;
    this.fetchAllSites();
  }

  ngAfterViewInit(): void {
    (window as any).vega = vega;
    (window as any).vegaLite = vegaLite;
    (window as any).vegaEmbed = vegaEmbed;
    const chartWrapper = this.el.nativeElement.querySelector('#chart-wrapper');
    if (chartWrapper) {
      this.renderer.listen(chartWrapper, 'scroll', () => {
        const nearEnd = chartWrapper.scrollLeft + chartWrapper.clientWidth >= chartWrapper.scrollWidth - 20;
        if (nearEnd && (this.currentChunk + 1) * this.CHUNK_SIZE < this.filteredData.length) {
          this.currentChunk++;
          this.renderChart();
        }
      });
    }
    if (this.hasCachedSites) {
      this.populateLosDropdown();
      this.applyFilter();
    }
  }

  buildLegendItems(data: any[]) {
    const losCounts: { [key: string]: number } = {};
    const sitesLosCounts: { [key: string]: number } = {};
    data.forEach(site => {
      const los = site.los || 'Other';
      const userCount = site.Users || 0;
      if (!losCounts[los]) losCounts[los] = 0;
      if (!sitesLosCounts[los]) sitesLosCounts[los] = 0;
      losCounts[los] += userCount;
      sitesLosCounts[los] += 1;
    });
    this.legendItems = Object.entries(this.losColorMap)
      .map(([label, color]) => ({
        label,
        color,
        sitesCount: sitesLosCounts[label] || 0,
        usersCount: losCounts[label] || 0
      }))
      .sort((a, b) => b.sitesCount - a.sitesCount);
  }

  async fetchAllSites() {
    const cached = localStorage.getItem(this.CACHE_KEY);
    if (cached) {
      this.allSites = JSON.parse(cached);
      this.buildLegendItems(this.allSites);
      if (!this.hasCachedSites) {
        this.populateLosDropdown();
        this.applyFilter();
      }
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    let page = 0;
    let total = 999999;
    let collected: any[] = [];
    while (page * this.PAGE_SIZE < total) {
      try {
        const url = `${environment.workSpaceAPI}/user/getSites?page=${page}&size=${this.PAGE_SIZE}`;
        const response = await fetch(url);
        const result = await response.json();
        if (result.success && Array.isArray(result.data.response)) {
          total = result.data.totalElements;
          const mapped = result.data.response.map((site: any) => {
            let userCount = 0;
            let userList = '';
            try {
              const parsedUsers = JSON.parse(site.users || '[]');
              if (Array.isArray(parsedUsers)) {
                userCount = parsedUsers.length;
                userList = parsedUsers.join(', ');
              }
            } catch {
              userList = 'Invalid user data';
            }
            return {
              'Site Name': site.siteName,
              'Users': userCount,
              'los': site.los || 'Unknown',
              'userList': userList
            };
          });
          collected = collected.concat(mapped);
          page++;
        } else {
          break;
        }
      } catch (error) {
        console.error('Fetch error:', error);
        break;
      }
    }
    this.allSites = collected;
    this.buildLegendItems(this.allSites);
    localStorage.setItem(this.CACHE_KEY, JSON.stringify(this.allSites));
    this.populateLosDropdown();
    this.applyFilter();
    this.isLoading = false;
  }

  populateLosDropdown() {
    const losSet = new Set(this.allSites.map(site => site.los).filter(Boolean));
    this.losOptions = Array.from(losSet).sort();
  }

  applyFilter() {
    this.isLoading = true;
    setTimeout(() => {
      const nameRegex = new RegExp(this.searchInput, 'i');
      const selectedLos = this.selectedLos;
      const pvNonPvFilter = this.pvNonPvFilter;
      this.filteredData = this.allSites.filter(site => {
        const matchesName = nameRegex.test(site['Site Name']);
        const matchesLos = selectedLos === '' || site.los === selectedLos;
        const users = site.userList.split(',').map((u: string) => u.trim()).filter(Boolean);
        const isPVUser = (email: string) => {
          let isPV = false;
          if (email.endsWith('@pro-vigil.com')) {
            isPV = true;
          } else if (email.indexOf('@') === -1) {
            isPV = true;
          }
          return isPV;
        };
        const totalUsers = users.length;
        const pvUserCount = users.filter(isPVUser).length;
        let passesUserFilter = true;
        if (pvNonPvFilter === 'pv') {
          passesUserFilter = pvUserCount === totalUsers;
        } else if (pvNonPvFilter === 'non-pv') {
          passesUserFilter = pvUserCount === 0;
        }
        return matchesName && matchesLos && passesUserFilter;
      });
      this.filteredData.sort((a, b) => b.Users - a.Users);
      this.currentChunk = 0;
      if (pvNonPvFilter !== '') {
        this.buildLegendItems(this.filteredData);
      } else {
        this.buildLegendItems(this.allSites);
      }
      this.renderChart();
      this.isLoading = false;
    });
  }

  renderChart() {
    if (!this.chartContainer || !this.chartContainer.nativeElement) {
      // Chart container not ready yet
      return;
    }
    const displayData = this.filteredData.slice(0, (this.currentChunk + 1) * this.CHUNK_SIZE);
    const spec = {
      $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
      description: 'Bar chart with Site Name, Users, los filter',
      data: { values: displayData },
      mark: 'bar',
      encoding: {
        x: {
          field: 'Site Name',
          type: 'nominal',
          sort: '-y',
          axis: { labelAngle: -45 }
        },
        y: { field: 'Users', type: 'quantitative' },
        tooltip: [
          { field: 'Site Name', type: 'nominal' },
          { field: 'Users', type: 'quantitative' },
          { field: 'los', type: 'nominal' },
          { field: 'userList', type: 'nominal', title: 'Users' }
        ],
        color: {
          field: 'los',
          type: 'nominal',
          scale: {
            domain: Object.keys(this.losColorMap),
            range: Object.values(this.losColorMap)
          },
          legend: null
        }
      },
      width: { step: this.barStep },
      height: 400
    };
    // @ts-ignore
    vegaEmbed(this.chartContainer.nativeElement, spec, { actions: false }).then((result: any) => {
      result.view.addEventListener('click', (event: any, item: any) => {
        if (item && item.datum && item.datum.userList) {
          const siteName = item.datum['Site Name'];
          const users = item.datum.userList.split(',').map((u: string) => u.trim()).filter(Boolean);
          this.openModal(siteName, users);
        }
      });
    });
  }

  zoomIn() {
    this.barStep += 5;
    this.renderChart();
  }

  zoomOut() {
    if (this.barStep > 5) {
      this.barStep -= 5;
      this.renderChart();
    }
  }

  clearCache() {
    this.isLoading = true;
    localStorage.removeItem(this.CACHE_KEY);
    this.hasCachedSites = false;
    this.fetchAllSites();
  }

  openModal(siteName: string, users: string[]) {
    this.dialog.open(this.userModalTpl, {
      data: { siteName: `Users at ${siteName}`, users }
    });
  }

  onLegendClick(los: string) {
    if (this.selectedLos === los) {
      this.selectedLos = '';
    } else {
      this.selectedLos = los;
    }
    this.applyFilter();
  }
}
